import { useStore } from '@hub/helpers/use-store';
import gtmService from '@hub/services/analytics/analytics';
import {
  getCheckoutEntryPath,
  getCheckoutSessionId,
  getCheckoutSource,
  trackMixpanel,
} from '@monorepo/client-common';
import {
  AnalyticsEventTypes,
  AssetSelectedProperties,
  BannerClickedProperties,
  CartOpenedProperties,
  CheckoutAddToCartFailedProperties,
  CheckoutAddToCartProperties,
  CheckoutClickedBackProperties,
  CheckoutClickedDeleteCartProperties,
  CheckoutCouponEventProperties,
  CheckoutOrderAgainInitiatedProperties,
  EventTypes,
  GroupSelectedProperties,
  PurchaseEventProperties,
} from '@monorepo/types';

export const usePurchaseEvents = () => {
  const {
    dataStore: { userStore },
  } = useStore();

  const handlePurchaseEvent = (
    event: EventTypes,
    properties: PurchaseEventProperties,
  ) => {
    switch (event) {
      case EventTypes.CHECKOUT_ASSET_SELECTED: {
        const props = properties as AssetSelectedProperties;
        const assetCheckoutEntryPath = getCheckoutEntryPath();
        trackMixpanel({
          event,
          properties: {
            asset: props.chosenAsset,
            cart_items_count: props.cart_items_count,
            checkout_context_state: props.checkout_context_state,
            checkout_session_id: props.checkout_session_id,
            ...(props.checkout_source && {
              checkout_source: props.checkout_source,
            }),
            ...(assetCheckoutEntryPath && {
              checkout_entry_path: assetCheckoutEntryPath,
            }),
          },
        });
        gtmService.sendEvent({
          event: AnalyticsEventTypes.AssetSelected,
          params: {
            asset: props.chosenAsset,
          },
        });
        break;
      }
      case EventTypes.CHECKOUT_GROUP_SELECTED: {
        const props = properties as GroupSelectedProperties;
        const checkoutSessionId = getCheckoutSessionId();
        const checkoutSource = getCheckoutSource();
        const checkoutEntryPath = getCheckoutEntryPath();
        trackMixpanel({
          event,
          properties: {
            group_name: props.groupName,
            asset: props.asset,
            cart_items_count: props.cart_items_count,
            checkout_context_state: props.checkout_context_state,
            ...(checkoutSessionId && {
              checkout_session_id: checkoutSessionId,
            }),
            ...(checkoutSource && { checkout_source: checkoutSource }),
            ...(checkoutEntryPath && {
              checkout_entry_path: checkoutEntryPath,
            }),
            ...(props.total_amount_usd !== undefined && {
              total_amount_usd: props.total_amount_usd,
            }),
            ...(props.total_amount_local !== undefined && {
              total_amount_local: props.total_amount_local,
            }),
            ...(props.currency && { currency: props.currency }),
          },
        });
        gtmService.sendEvent({
          event: AnalyticsEventTypes.GroupSelected,
          params: {
            group_name: props.groupName,
          },
        });
        break;
      }
      case EventTypes.CHECKOUT_CLICKED_BACK: {
        const props = properties as CheckoutClickedBackProperties;
        if (!props.checkout_session_id || !props.checkout_entry_path) {
          return;
        }

        trackMixpanel({
          event,
          properties: {
            current_step: props.current_step,
            target_step: props.target_step,
            checkout_session_id: props.checkout_session_id,
            checkout_entry_path: props.checkout_entry_path,
            checkout_context_state: props.checkout_context_state,
            cart_items_count: props.cart_items_count,
            ...props.stepProperties,
          },
        });
        break;
      }
      case EventTypes.CHECKOUT_CLICKED_DELETE_CART: {
        const props = properties as CheckoutClickedDeleteCartProperties;
        if (!props.checkout_session_id || !props.checkout_entry_path) {
          return;
        }

        trackMixpanel({
          event,
          properties: {
            checkout_session_id: props.checkout_session_id,
            checkout_entry_path: props.checkout_entry_path,
            checkout_context_state: props.checkout_context_state,
            checkout_source: props.checkout_source,
            cart_items_count: props.cart_items_count,
            ...props.cartProperties,
          },
        });
        break;
      }
      case EventTypes.CHECKOUT_CLICKED_BANNER: {
        const props = properties as BannerClickedProperties;
        trackMixpanel({
          event,
          properties: {
            checkout_session_id: props.checkout_session_id,
            checkout_entry_path: props.checkout_entry_path,
            checkout_source: props.checkout_source,
            banner_id: props.banner_id,
            banner_text: props.banner_text,
            destination_url: props.destination_url,
            device: props.device,
            brand: props.brand,
            ...(props.program_id && { program_id: props.program_id }),
          },
        });
        break;
      }
      case EventTypes.CHECKOUT_CLICKED_ADD_TO_CART:
      case EventTypes.CHECKOUT_ADD_TO_CART_FAILED: {
        const props = properties as
          | CheckoutAddToCartProperties
          | CheckoutAddToCartFailedProperties;
        trackMixpanel({
          event,
          properties: {
            asset: props.asset,
            group_name: props.group_name,
            buying_power: props.buying_power,
            trading_system: props.trading_system,
            total_amount_usd: props.total_amount_usd,
            total_amount_local: props.total_amount_local,
            currency: props.currency,
            cart_items_count: props.cart_items_count,
            checkout_entry_path: props.checkout_entry_path,
            checkout_session_id: props.checkout_session_id,
            checkout_source: props.checkout_source,
            checkout_context_state: props.checkout_context_state,
            // Include error_type only for failed events
            ...('error_type' in props && { error_type: props.error_type }),
          },
        });
        break;
      }
      case EventTypes.CART_OPENED: {
        const props = properties as CartOpenedProperties;
        trackMixpanel({
          event,
          properties: {
            user_id: userStore.currentUser._id,
            properties: {
              ...props,
            },
          },
        });
        break;
      }
      case EventTypes.CHECKOUT_COUPON_ADD:
      case EventTypes.CHECKOUT_COUPON_SELECT: {
        const props = properties as CheckoutCouponEventProperties;
        trackMixpanel({
          event,
          properties: {
            checkout_session_id: props.checkout_session_id,
            checkout_entry_path: props.checkout_entry_path,
            checkout_source: props.checkout_source,
            coupon_name: props.coupon_name,
            is_success: props.is_success,
          },
        });
        break;
      }
      case EventTypes.CHECKOUT_ORDER_AGAIN_INITIATED: {
        const props = properties as CheckoutOrderAgainInitiatedProperties;
        trackMixpanel({
          event,
          properties: {
            checkout_session_id: props.checkout_session_id,
            checkout_entry_path: props.checkout_entry_path,
            checkout_context_state: props.checkout_context_state,
            checkout_source: props.checkout_source,
            cart_items_count: props.cart_items_count,
            asset: props.asset,
            group_name: props.group_name,
            buying_power: props.buying_power,
            trading_system: props.trading_system,
            total_amount_local: props.total_amount_local,
            total_amount_usd: props.total_amount_usd,
            currency: props.currency,
          },
        });
        break;
      }
    }
  };

  return { handlePurchaseEvent };
};
